import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Field, TextInput, ModalShell, DeleteButton } from "./ui";

const EMPTY = {
  title: "", excerpt: "", content: "", cover_image: "", category: "Analysis",
  author: "TerminalEdge Desk", read_time: "5 min read",
};

export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/blog").then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/blog", form);
        toast.success("ARTICLE PUBLISHED");
      } else {
        await api.put(`/admin/blog/${editing.id}`, form);
        toast.success("ARTICLE UPDATED");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("ARTICLE PURGED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-blog-tab">
      <div className="mb-6 flex justify-end">
        <button onClick={openNew} data-testid="add-post-btn"
          className="btn-beam inline-flex items-center gap-2 bg-signal px-5 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.2em] text-ink">
          <Plus size={13} /> New Article
        </button>
      </div>
      <div className="space-y-px border border-white/10 bg-white/10" data-testid="admin-posts-list">
        {posts.length === 0 && (
          <p className="bg-ink p-7 font-mono text-xs uppercase tracking-[0.2em] text-muted">No articles yet.</p>
        )}
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-5 bg-ink p-5" data-testid={`admin-post-${p.slug}`}>
            {p.cover_image && <img src={p.cover_image} alt="" className="hidden h-14 w-24 shrink-0 border border-line object-cover sm:block" />}
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-signal">{p.category} · {p.read_time}</p>
              <h3 className="mt-1 truncate font-display text-base font-700 text-white">{p.title}</h3>
              <p className="mt-0.5 truncate text-xs text-muted">{p.excerpt}</p>
            </div>
            <span className="flex shrink-0 gap-2">
              <button onClick={() => openEdit(p)} data-testid={`edit-post-${p.slug}`}
                className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors hover:border-signal hover:text-signal">
                <Pencil size={11} /> Edit
              </button>
              <DeleteButton testId={`delete-post-${p.slug}`} onConfirm={() => remove(p)} />
            </span>
          </div>
        ))}
      </div>

      {editing && (
        <ModalShell wide title={editing === "new" ? "NEW ARTICLE" : "EDIT ARTICLE"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="post-form">
            <div className="sm:col-span-2"><Field label="Title *"><TextInput required data-testid="post-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field></div>
            <div className="sm:col-span-2"><Field label="Excerpt"><TextInput data-testid="post-form-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></Field></div>
            <Field label="Category"><TextInput value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Read Time"><TextInput value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Cover Image URL"><TextInput data-testid="post-form-cover" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://" /></Field></div>
            <div className="sm:col-span-2">
              <Field label="Content (blank line between paragraphs)">
                <textarea rows={10} data-testid="post-form-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="mt-1.5 w-full resize-none border border-line bg-ink px-3 py-2.5 text-sm leading-relaxed text-white outline-none focus:border-signal" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={busy} data-testid="post-form-save"
                className="btn-beam w-full bg-signal px-5 py-3 font-mono text-[11px] font-700 uppercase tracking-[0.25em] text-ink transition-opacity disabled:opacity-50">
                {busy ? "Saving…" : editing === "new" ? "Publish" : "Save Changes"}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}
